import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import Loading from '@/pages/dashboard/components/Loading';
import { IAnomalyReportListState } from '../../hooks/useAnomalyReportList';
import {
  IAnomalyReportItem,
  pickAnomalyReport,
  resolveAnomalyReportLocale,
} from '../../utils';
import './index.scss';
import IframeRender from '@/pages/dashboard/components/IframeRender';

type TInitialFilterState = 'loading' | 'error' | 'ready';

interface IAbnormalReportIframeCoProps {
  reportState?: IAnomalyReportListState;
  initialFilterState?: TInitialFilterState;
  style?: React.CSSProperties;
  className?: string;
}

const AbnormalReportIframeCo: React.FC<IAbnormalReportIframeCoProps> = ({
  reportState,
  initialFilterState = 'loading',
  style,
  className,
}) => {
  const { locale } = useIntl();
  const [iframeLoading, setIframeLoading] = useState<boolean>(true);
  const safeReportState: IAnomalyReportListState = reportState || {
    loading: false,
    data: null,
    errorMsg: '',
  };

  const reportInfo = useMemo(() => {
    const list = safeReportState.data as IAnomalyReportItem[] | undefined;
    const reportLocale = resolveAnomalyReportLocale(locale);
    return pickAnomalyReport(list, reportLocale);
  }, [safeReportState.data, locale]);

  const reportUrl = reportInfo?.url || '';

  useEffect(() => {
    if (!reportUrl) {
      setIframeLoading(false);
      return;
    }

    setIframeLoading(true);
  }, [reportUrl]);

  const handleIframeLoaded = (): void => {
    setIframeLoading(false);
  };

  if (initialFilterState === 'error') {
    return null;
  }

  const isInitialLoading = initialFilterState === 'loading';
  const isApiLoading = isInitialLoading || safeReportState.loading;
  const shouldRenderIframe = !isApiLoading && !!reportUrl;
  const showIframeLoading = !isApiLoading && !!reportUrl && iframeLoading;

  return (
    <div
      className={classNames('dbd-abnormal-report-iframe-co', className)}
      style={style}
    >
      <Abnormal
        isLoading={isApiLoading}
        isError={!isInitialLoading && !!safeReportState.errorMsg}
        isNoData={
          !isInitialLoading &&
          !reportUrl &&
          !safeReportState.loading &&
          !safeReportState.errorMsg
        }
        advanceRender={false}
        style={{ height: '100%' }}
        component={
          <div className="dbd-abnormal-report-iframe-wrap">
            {showIframeLoading && (
              <div className="dbd-abnormal-report-iframe-wrap__loading">
                <Loading />
              </div>
            )}
            {shouldRenderIframe && (
              <IframeRender
                key={reportUrl || locale}
                className={classNames({
                  'dbd-abnormal-report-iframe-wrap__iframe--loading':
                    showIframeLoading,
                })}
                url={reportUrl}
                onLoad={handleIframeLoaded}
                onError={handleIframeLoaded}
              />
            )}
          </div>
        }
      />
    </div>
  );
};

export default AbnormalReportIframeCo;
